import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';

import type { User } from '@keboola/api-client/developerPortal/types';
import {
  AlertModal,
  Badge,
  Button,
  EmptyState,
  PageHeader,
  PageLayout,
  PageToolbar,
  SearchInput,
  Skeleton,
  Table,
  toast,
} from '@keboola/design';

import { useAuth, useCurrentVendor } from '../../auth/context';
import { QueryError } from '../../components/QueryError';
import { getApiErrorMessage } from '../../features/auth/errors';
import { InviteDeveloperModal } from '../../features/vendors/components/InviteDeveloperModal';
import { filterUsers, isSelf } from '../../features/vendors/members';
import {
  useRemoveVendorUser,
  useServiceAccounts,
  useVendorUsers,
} from '../../features/vendors/queries';

export const Route = createFileRoute('/~/developers')({
  component: DevelopersPage,
  head: () => ({ meta: [{ title: 'Developers | Keboola Developer Portal' }] }),
});

/**
 * Confirms removing a member or leaving the vendor.
 *
 * - Leaving rotates the id token (drops the vendor from `profile`) and can trigger the select-vendor gate.
 * - One dialog keeps both copies in step with what actually happens.
 */
function RemoveMemberButton({
  user,
  leaving,
  isServiceAccount,
}: {
  user: User;
  leaving: boolean;
  isServiceAccount: boolean;
}) {
  const vendorId = useCurrentVendor();
  const removeUser = useRemoveVendorUser(vendorId);

  return (
    <AlertModal
      // Caught: `AlertModal` closes once the promise settles, so a rejection would close silently
      // and look like a removal that worked.
      onConfirm={async () => {
        try {
          await removeUser.mutateAsync({ username: user.username, isSelf: leaving });
        } catch (error) {
          toast.error(
            getApiErrorMessage(
              error,
              leaving ? 'Could not leave the vendor.' : 'Could not remove the developer.',
            ),
          );
          return;
        }
        toast.success(
          leaving ? `You have left ${vendorId}.` : `${user.username} was removed from ${vendorId}.`,
        );
      }}
    >
      <AlertModal.Trigger asChild>
        <Button variant="danger" size="small">
          {leaving ? 'Leave' : 'Remove'}
        </Button>
      </AlertModal.Trigger>
      <AlertModal.Content
        variant="danger"
        title={leaving ? `Leave ${vendorId}?` : `Remove ${user.username}?`}
        confirmLabel={
          leaving ? 'Leave vendor' : isServiceAccount ? 'Delete account' : 'Remove developer'
        }
      >
        {/* ⚠️ A removed service account cannot be invited back: its credentials are destroyed. */}
        {leaving
          ? `You will lose access to every component belonging to ${vendorId}. Another member has to invite you back.`
          : isServiceAccount
            ? `Its credentials stop working immediately and cannot be restored — anything using them to reach the Developer Portal as ${vendorId} will break. A replacement account gets new credentials.`
            : `They will lose access to every component belonging to ${vendorId}. You can invite them again at any time.`}
      </AlertModal.Content>
    </AlertModal>
  );
}

function DevelopersPage() {
  const vendorId = useCurrentVendor();
  const { identity } = useAuth();
  const { data: users, isLoading, isError, error, refetch } = useVendorUsers(vendorId);
  // `?service=1` is what defines a service account; the optional `description` is no signal.
  // Same endpoint and invalidation as the list above.
  const { data: serviceAccounts } = useServiceAccounts(vendorId);
  const serviceAccountNames = useMemo(
    () => new Set((serviceAccounts ?? []).map((account) => account.username)),
    [serviceAccounts],
  );
  const [search, setSearch] = useState('');

  const visible = useMemo(() => filterUsers(users ?? [], search), [users, search]);

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageHeader.Index
          title="Developers"
          description={`Everyone who can manage ${vendorId}'s components.`}
          action={<InviteDeveloperModal vendorId={vendorId} />}
        />
      </PageLayout.Header>

      {isLoading && <Skeleton className="tw:h-64" />}

      {/* - Blocking only with no data: a failed refetch keeps the previous list readable.
          - `as="h2"`: sits under the page header's `h1`. */}
      <QueryError
        isError={isError}
        hasData={users !== undefined}
        error={error}
        onRetry={() => void refetch()}
        loadTitle="Could not load the developers"
        refreshTitle="Could not refresh the developers"
        staleNote="Showing the developers from the last successful load."
        titleAs="h2"
      />

      {users !== undefined && (
        <>
          <PageToolbar>
            <PageToolbar.Search>
              <SearchInput
                allowClear
                placeholder="Search by name or email"
                value={search}
                onValueChange={setSearch}
              />
            </PageToolbar.Search>
          </PageToolbar>

          {visible.length === 0 ? (
            <EmptyState variant="compact">
              <EmptyState.Description>
                {search === ''
                  ? 'This vendor has no developers.'
                  : `No developer matches “${search}”.`}
              </EmptyState.Description>
            </EmptyState>
          ) : (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>Developer</Table.Head>
                  <Table.Head>
                    <span className="tw:sr-only">Actions</span>
                  </Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {visible.map((user) => {
                  const leaving = isSelf(user, identity?.email ?? null);
                  // Only a non-empty string is worth showing as a name.
                  const name = user.name ?? '';
                  const isServiceAccount = serviceAccountNames.has(user.username);
                  return (
                    <Table.Row key={user.username}>
                      <Table.Cell>
                        <div className="tw:flex tw:flex-col">
                          <span className="tw:flex tw:items-center tw:gap-2 tw:font-medium">
                            {name === '' ? user.username : name}
                            {leaving && <Badge color="purple-inverse">You</Badge>}
                            {isServiceAccount && (
                              <Badge color="gray-inverse">Service account</Badge>
                            )}
                          </span>
                          {/* Only when the name adds something: the fallback above already shows
                              the address. */}
                          {name !== '' && (
                            <span className="tw:text-xs tw:text-muted-foreground">
                              {user.username}
                            </span>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="tw:flex tw:justify-end">
                          <RemoveMemberButton
                            user={user}
                            leaving={leaving}
                            isServiceAccount={isServiceAccount}
                          />
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          )}
        </>
      )}
    </PageLayout>
  );
}
